import TransactionType from './types/TransactionType'

/**
 * Turns a string TransactionType into an enum TransactionType.
 *
 * @throws Throws an error if the given transaction type is not valid.
 * @param input - The transaction type.
 *
 * @returns The transaction type as an enum.
 */
export function validateTransactionType(input: string): TransactionType {
  const possibleType = input as TransactionType
  if (!Object.values(TransactionType).includes(possibleType)) {
    throw new Error(`Invalid TransactionType ${input}`)
  }

  return possibleType
}

/**
 * Takes a hex string and decodes it into Ascii.
 *
 * @param input - The hex input.
 * @returns The ascii string.
 */
export function hexToAscii(input: string): string {
  return Buffer.from(input, 'hex').toString()
}

/**
 * Taken from https://xrpl.org/basic-data-types.html#specifying-time.
 * Times in XRP ledger occur this many seconds after the UNIX epoch.
 */
const rippleEpoch = 946684800

/**
 * Converts a time into a human readable string.
 * The time is given as the number of seconds after the Ripple epoch.
 *
 * @param time - The number of seconds after the Ripple epoch.
 * @returns The time as a UTC time string.
 */
export function parseDate(time: number): string {
  const date = new Date((rippleEpoch + time) * 1000)
  return date.toUTCString()
}

/**
 * Turn a number into a rank. E.g.:
 * 1 => 1st
 * 2 => 2nd
 * 3 => 3rd
 * 4 => 4th
 * etc.
 *
 * @param num - The number.
 * @returns Result.
 */
export function numberToRankString(num: number): string {
  let suffix = 'th'
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Not truly a magic number in this case.
  if (num % 10 === 1 && num % 100 !== 11) {
    suffix = 'st'
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Not truly a magic number in this case.
  } else if (num % 10 === 2 && num % 100 !== 12) {
    suffix = 'nd'
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Not truly a magic number in this case.
  } else if (num % 10 === 3 && num % 100 !== 13) {
    suffix = 'rd'
  }

  return `${num}${suffix}`
}
